import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

// Configuración de servicios de cookies
const cookieServices = {
  analytics: {
    name: 'Google Analytics',
    description: 'Nos ayuda a entender cómo los usuarios interactúan con nuestro sitio web'
  },
  functional: {
    name: 'Brevo & Chatbot',
    description: 'Permite el funcionamiento de formularios de contacto y chat en línea'
  },
  marketing: {
    name: 'Cookies de Marketing',
    description: 'Se utilizan para mostrar anuncios relevantes y medir campañas'
  }
};

const CookieBannerWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${colors.white};
  border-top: 1px solid rgba(45, 31, 64, 0.1);
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  padding: 25px 20px;
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.4s ease-in-out;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 30px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    text-align: center;
  }
`;

const BannerText = styled.div`
  font-family: ${fonts.inter};
`;

const BannerTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.primary};
  margin-bottom: 8px;
`;

const BannerDescription = styled.p`
  font-size: 0.9rem;
  color: ${colors.text};
  line-height: 1.5;
  margin-bottom: 10px;
`;

const BannerLinks = styled.div`
  font-size: 0.85rem;
  
  a {
    color: ${colors.secondary};
    text-decoration: none;
    margin-right: 15px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BannerActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button`
  font-family: ${fonts.inter};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 25px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
          color: ${colors.white};
          box-shadow: 0 4px 15px rgba(243, 156, 107, 0.3);
          
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(243, 156, 107, 0.4);
          }
        `;
      case 'secondary':
        return `
          background: ${colors.primary};
          color: ${colors.white};
          
          &:hover {
            background: ${colors.darkBackground};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${colors.text};
          border: 1px solid rgba(45, 31, 64, 0.3);
          
          &:hover {
            background: ${colors.background};
          }
        `;
      default:
        return '';
    }
  }}

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 20px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const Modal = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 30px;
`;

const ModalTitle = styled.h2`
  font-family: ${fonts.playfair};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.text};
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const CookieCategory = styled.div`
  margin-bottom: 25px;
  padding: 20px;
  border: 1px solid rgba(45, 31, 64, 0.1);
  border-radius: 12px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CategoryTitle = styled.h3`
  font-family: ${fonts.inter};
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.primary};
`;

const CategoryDescription = styled.p`
  font-family: ${fonts.inter};
  font-size: 0.9rem;
  color: ${colors.text};
  line-height: 1.5;
  opacity: 0.8;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.checked ? colors.accent : '#ccc'};
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: ${props => props.checked ? '23px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false
  });

  // Cargar preferencias del localStorage al montar el componente
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(parsed);
        // No mostrar banner si ya hay preferencias guardadas
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
        setShowBanner(true);
      }
    } else {
      // Mostrar banner si no hay preferencias guardadas
      setShowBanner(true);
    }
  }, []);

  const savePreferences = (prefs) => {
    const preferencesWithTimestamp = {
      ...prefs,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    localStorage.setItem('cookiePreferences', JSON.stringify(preferencesWithTimestamp));
    setPreferences(prefs);
    setShowBanner(false);
    setShowModal(false);

    // Aplicar las preferencias a los servicios
    applyCookiePreferences(prefs);
  };

  const applyCookiePreferences = (prefs) => {
    // Google Analytics
    if (prefs.analytics) {
      // Activar Google Analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
    } else {
      // Desactivar Google Analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
    }

    // Otras cookies funcionales y de marketing se pueden gestionar aquí
    localStorage.setItem('cookieFunctional', prefs.functional.toString());
    localStorage.setItem('cookieMarketing', prefs.marketing.toString());
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const rejectOptional = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    savePreferences(onlyNecessary);
  };

  const openCustomize = () => {
    setShowModal(true);
  };

  const handleToggleChange = (category) => {
    if (category === 'necessary') return; // No se puede desactivar

    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  // Función pública para resetear preferencias (útil para testing)
  const resetPreferences = () => {
    localStorage.removeItem('cookiePreferences');
    localStorage.removeItem('cookieFunctional');
    localStorage.removeItem('cookieMarketing');
    setShowBanner(true);
  };

  // Exposer función globalmente para acceso desde consola o otros componentes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.resetCookiePreferences = resetPreferences;
    }
  }, []);

  return (
    <>
      <CookieBannerWrapper show={showBanner}>
        <BannerContent>
          <BannerText>
            <BannerTitle>🍪 Utilizamos cookies para mejorar tu experiencia</BannerTitle>
            <BannerDescription>
              Utilizamos cookies propias y de terceros para analizar el uso del sitio web y mostrarte 
              publicidad relacionada con tus preferencias.
            </BannerDescription>
            <BannerLinks>
              <a href="/politica-cookies" target="_blank">Política de Cookies</a>
              <a href="/politica-privacidad" target="_blank">Política de Privacidad</a>
            </BannerLinks>
          </BannerText>
          
          <BannerActions>
            <Button variant="outline" onClick={rejectOptional}>
              Solo necesarias
            </Button>
            <Button variant="secondary" onClick={openCustomize}>
              Personalizar
            </Button>
            <Button variant="primary" onClick={acceptAll}>
              Aceptar todas
            </Button>
          </BannerActions>
        </BannerContent>
      </CookieBannerWrapper>

      <ModalOverlay show={showModal}>
        <Modal>
          <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
          
          <ModalHeader>
            <ModalTitle>Personalizar preferencias de cookies</ModalTitle>
            <CategoryDescription>
              Elige qué tipos de cookies quieres permitir. Ten en cuenta que deshabilitar algunas 
              cookies puede afectar la funcionalidad del sitio web.
            </CategoryDescription>
          </ModalHeader>

          <CookieCategory>
            <CategoryHeader>
              <CategoryTitle>Cookies necesarias</CategoryTitle>
              <Toggle disabled>
                <ToggleInput type="checkbox" checked disabled />
                <ToggleSlider checked />
              </Toggle>
            </CategoryHeader>
            <CategoryDescription>
              Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.
              Incluyen cookies de sesión, seguridad y funcionalidades básicas.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryHeader>
              <CategoryTitle>Cookies de análisis</CategoryTitle>
              <Toggle>
                <ToggleInput 
                  type="checkbox" 
                  checked={preferences.analytics}
                  onChange={() => handleToggleChange('analytics')}
                />
                <ToggleSlider checked={preferences.analytics} />
              </Toggle>
            </CategoryHeader>
            <CategoryDescription>
              {cookieServices.analytics.description}. Utilizamos Google Analytics para estos fines.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryHeader>
              <CategoryTitle>Cookies funcionales</CategoryTitle>
              <Toggle>
                <ToggleInput 
                  type="checkbox" 
                  checked={preferences.functional}
                  onChange={() => handleToggleChange('functional')}
                />
                <ToggleSlider checked={preferences.functional} />
              </Toggle>
            </CategoryHeader>
            <CategoryDescription>
              {cookieServices.functional.description}. Incluye servicios como Brevo y el chatbot.
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryHeader>
              <CategoryTitle>Cookies de marketing</CategoryTitle>
              <Toggle>
                <ToggleInput 
                  type="checkbox" 
                  checked={preferences.marketing}
                  onChange={() => handleToggleChange('marketing')}
                />
                <ToggleSlider checked={preferences.marketing} />
              </Toggle>
            </CategoryHeader>
            <CategoryDescription>
              {cookieServices.marketing.description}. Actualmente no utilizamos estas cookies, 
              pero pueden activarse en el futuro.
            </CategoryDescription>
          </CookieCategory>

          <ModalActions>
            <Button variant="outline" onClick={rejectOptional}>
              Solo necesarias
            </Button>
            <Button variant="primary" onClick={saveCustomPreferences}>
              Guardar preferencias
            </Button>
          </ModalActions>
        </Modal>
      </ModalOverlay>
    </>
  );
}

// Funciones de utilidad para otros componentes
export const getCookiePreference = (category) => {
  if (typeof window === 'undefined') return false;
  
  const savedPreferences = localStorage.getItem('cookiePreferences');
  if (savedPreferences) {
    try {
      const parsed = JSON.parse(savedPreferences);
      return parsed[category] || false;
    } catch (error) {
      console.error('Error parsing cookie preferences:', error);
      return false;
    }
  }
  return false;
};

export const setCookiePreference = (category, value) => {
  if (typeof window === 'undefined') return;
  
  const savedPreferences = localStorage.getItem('cookiePreferences');
  let preferences = {
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false
  };
  
  if (savedPreferences) {
    try {
      preferences = JSON.parse(savedPreferences);
    } catch (error) {
      console.error('Error parsing cookie preferences:', error);
    }
  }
  
  preferences[category] = value;
  
  const preferencesWithTimestamp = {
    ...preferences,
    timestamp: Date.now(),
    version: '1.0'
  };
  
  localStorage.setItem('cookiePreferences', JSON.stringify(preferencesWithTimestamp));
};

export default CookieBanner; 